/**
 * Sync Context
 * Manages cloud sync state, locking, retry queue, and status tracking.
 *
 * Phase 4 Features:
 * - Sync locking to prevent concurrent syncs
 * - Token refresh before sync operations
 * - Retry queue for failed operations
 * - Abort handling for sign-out during sync
 * - User-facing sync status
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import { supabase, isSupabaseConfigured } from '@/config/supabase';
import { syncProjects, syncDesigns, uploadAutoSaveToCloud } from '@/services/supabaseSync';
import { migrateDesignsToUser, migrateProjectsToUser } from '@/services/storage';
import type { AutoSaveEntry } from '@/services/storage';

// ============================================
// Types
// ============================================

export type SyncStatus = 'idle' | 'syncing' | 'success' | 'error' | 'offline';

export interface SyncResult {
  uploaded: number;
  downloaded: number;
  conflicts: number;
  errors: string[];
}

export interface RetryItem {
  id: string;
  type: 'autosave' | 'project' | 'design';
  data: unknown;
  attempts: number;
  lastAttempt: Date;
  error?: string;
}

interface SyncState {
  status: SyncStatus;
  lastSyncAt: Date | null;
  lastResult: SyncResult | null;
  error: string | null;
  retryQueue: RetryItem[];
  isMigrated: boolean;
}

type SyncAction =
  | { type: 'SET_STATUS'; payload: SyncStatus }
  | { type: 'SET_LAST_SYNC'; payload: { at: Date; result: SyncResult } }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'ADD_TO_RETRY_QUEUE'; payload: RetryItem }
  | { type: 'REMOVE_FROM_RETRY_QUEUE'; payload: string }
  | { type: 'UPDATE_RETRY_ITEM'; payload: { id: string; updates: Partial<RetryItem> } }
  | { type: 'CLEAR_RETRY_QUEUE' }
  | { type: 'SET_MIGRATED'; payload: boolean };

export interface SyncContextValue {
  status: SyncStatus;
  lastSyncAt: Date | null;
  lastResult: SyncResult | null;
  error: string | null;
  retryQueueLength: number;
  isSyncing: boolean;
  triggerSync: () => Promise<void>;
  queueAutoSave: (entry: AutoSaveEntry) => Promise<void>;
  clearError: () => void;
}

// ============================================
// Constants
// ============================================

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 5000; // 5 seconds between retries
const SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // Refresh if expiring in <5 min

// ============================================
// Reducer
// ============================================

const initialState: SyncState = {
  status: 'idle',
  lastSyncAt: null,
  lastResult: null,
  error: null,
  retryQueue: [],
  isMigrated: false,
};

function syncReducer(state: SyncState, action: SyncAction): SyncState {
  switch (action.type) {
    case 'SET_STATUS':
      return {
        ...state,
        status: action.payload,
        error: action.payload === 'error' ? state.error : null,
      };
    case 'SET_LAST_SYNC':
      return { ...state, lastSyncAt: action.payload.at, lastResult: action.payload.result };
    case 'SET_ERROR':
      return { ...state, error: action.payload, status: action.payload ? 'error' : state.status };
    case 'ADD_TO_RETRY_QUEUE':
      return { ...state, retryQueue: [...state.retryQueue, action.payload] };
    case 'REMOVE_FROM_RETRY_QUEUE':
      return {
        ...state,
        retryQueue: state.retryQueue.filter((item) => item.id !== action.payload),
      };
    case 'UPDATE_RETRY_ITEM':
      return {
        ...state,
        retryQueue: state.retryQueue.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload.updates } : item
        ),
      };
    case 'CLEAR_RETRY_QUEUE':
      return { ...state, retryQueue: [] };
    case 'SET_MIGRATED':
      return { ...state, isMigrated: action.payload };
    default:
      return state;
  }
}

// ============================================
// Context
// ============================================

const SyncContext = createContext<SyncContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useSyncContext(): SyncContextValue {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSyncContext must be used within SyncProvider');
  }
  return context;
}

/**
 * Safe hook that returns null if used outside provider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSyncContextSafe(): SyncContextValue | null {
  return useContext(SyncContext);
}

// ============================================
// Provider
// ============================================

export interface SyncProviderProps {
  children: ReactNode;
  userId: string | null;
}

export function SyncProvider({ children, userId }: SyncProviderProps) {
  const [state, dispatch] = useReducer(syncReducer, initialState);
  const syncLockRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Refresh Supabase session if it's about to expire
   */
  const ensureFreshToken = useCallback(async (): Promise<boolean> => {
    if (!isSupabaseConfigured()) return false;

    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error || !session) {
        console.warn('[SyncContext] No valid session');
        return false;
      }

      // Check if token expires soon
      const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
      const now = Date.now();

      if (expiresAt - now < TOKEN_REFRESH_THRESHOLD_MS) {
        console.warn('[SyncContext] Token expiring soon, refreshing...');
        const { error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError) {
          console.error('[SyncContext] Token refresh failed:', refreshError);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('[SyncContext] Token check failed:', error);
      return false;
    }
  }, []);

  /**
   * Process retry queue
   */
  const processRetryQueue = useCallback(async () => {
    if (state.retryQueue.length === 0) return;

    for (const item of state.retryQueue) {
      if (item.attempts >= MAX_RETRY_ATTEMPTS) {
        console.warn('[SyncContext] Max retries exceeded, removing from queue:', item.id);
        dispatch({ type: 'REMOVE_FROM_RETRY_QUEUE', payload: item.id });
        continue;
      }

      try {
        switch (item.type) {
          case 'autosave':
            await uploadAutoSaveToCloud(item.data as AutoSaveEntry);
            dispatch({ type: 'REMOVE_FROM_RETRY_QUEUE', payload: item.id });
            break;
          // Add more types as needed
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Retry failed';
        dispatch({
          type: 'UPDATE_RETRY_ITEM',
          payload: {
            id: item.id,
            updates: {
              attempts: item.attempts + 1,
              lastAttempt: new Date(),
              error: msg,
            },
          },
        });
      }
    }
  }, [state.retryQueue]);

  /**
   * Perform sync with locking and token refresh
   */
  const performSync = useCallback(async () => {
    if (!userId || !isSupabaseConfigured()) {
      return;
    }

    // Acquire lock
    if (syncLockRef.current) {
      console.warn('[SyncContext] Sync already in progress, skipping');
      return;
    }

    syncLockRef.current = true;
    abortControllerRef.current = new AbortController();
    dispatch({ type: 'SET_STATUS', payload: 'syncing' });

    const errors: string[] = [];
    let uploaded = 0;
    let downloaded = 0;
    let conflicts = 0;

    try {
      // Refresh token if needed
      const hasValidToken = await ensureFreshToken();
      if (!hasValidToken) {
        throw new Error('Unable to refresh authentication');
      }

      // Check for abort
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Sync aborted');
      }

      // First-time migration
      if (!state.isMigrated) {
        const [migratedDesigns, migratedProjects] = await Promise.all([
          migrateDesignsToUser(userId),
          migrateProjectsToUser(userId),
        ]);

        if (migratedDesigns > 0 || migratedProjects > 0) {
          console.warn('[SyncContext] Migrated orphan data:', {
            designs: migratedDesigns,
            projects: migratedProjects,
          });
        }

        dispatch({ type: 'SET_MIGRATED', payload: true });
      }

      // Check for abort
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Sync aborted');
      }

      // Sync projects
      try {
        const projectsResult = await syncProjects(userId);
        uploaded += projectsResult.uploaded;
        downloaded += projectsResult.downloaded;
        conflicts += projectsResult.conflicts;
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Project sync failed';
        errors.push(msg);
        console.error('[SyncContext] Project sync error:', error);
      }

      // Check for abort
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Sync aborted');
      }

      // Sync designs
      try {
        const designsResult = await syncDesigns(userId);
        uploaded += designsResult.uploaded;
        downloaded += designsResult.downloaded;
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Design sync failed';
        errors.push(msg);
        console.error('[SyncContext] Design sync error:', error);
      }

      // Process retry queue
      await processRetryQueue();

      const result: SyncResult = { uploaded, downloaded, conflicts, errors };
      dispatch({
        type: 'SET_LAST_SYNC',
        payload: { at: new Date(), result },
      });
      dispatch({ type: 'SET_STATUS', payload: errors.length > 0 ? 'error' : 'success' });

      if (errors.length > 0) {
        dispatch({ type: 'SET_ERROR', payload: errors.join('; ') });
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Sync failed';
      console.error('[SyncContext] Sync failed:', error);
      dispatch({ type: 'SET_ERROR', payload: msg });
      dispatch({ type: 'SET_STATUS', payload: 'error' });
    } finally {
      syncLockRef.current = false;
      abortControllerRef.current = null;
    }
  }, [userId, state.isMigrated, ensureFreshToken, processRetryQueue]);

  /**
   * Queue auto-save for upload with retry support
   */
  const queueAutoSave = useCallback(
    async (entry: AutoSaveEntry) => {
      if (!userId || !isSupabaseConfigured()) {
        return;
      }

      try {
        // Ensure fresh token
        const hasValidToken = await ensureFreshToken();
        if (!hasValidToken) {
          throw new Error('Authentication expired');
        }

        await uploadAutoSaveToCloud(entry);
      } catch (error) {
        console.warn('[SyncContext] Auto-save upload failed, queuing for retry:', error);

        const retryItem: RetryItem = {
          id: entry.id,
          type: 'autosave',
          data: entry,
          attempts: 1,
          lastAttempt: new Date(),
          error: error instanceof Error ? error.message : 'Upload failed',
        };

        dispatch({ type: 'ADD_TO_RETRY_QUEUE', payload: retryItem });

        // Schedule retry
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
        retryTimeoutRef.current = setTimeout(() => {
          processRetryQueue();
        }, RETRY_DELAY_MS);
      }
    },
    [userId, ensureFreshToken, processRetryQueue]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  // Initial sync and interval
  useEffect(() => {
    if (!userId || !isSupabaseConfigured()) {
      dispatch({ type: 'SET_STATUS', payload: 'idle' });
      return;
    }

    // Initial sync
    performSync();

    // Background sync interval
    const intervalId = setInterval(performSync, SYNC_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);

      // Abort any in-progress sync on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [userId, performSync]);

  // Handle sign-out: abort pending syncs and clear queue
  useEffect(() => {
    if (!userId) {
      // User signed out
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      dispatch({ type: 'CLEAR_RETRY_QUEUE' });
      dispatch({ type: 'SET_MIGRATED', payload: false });
      dispatch({ type: 'SET_STATUS', payload: 'idle' });
    }
  }, [userId]);

  // Network status detection
  useEffect(() => {
    const handleOnline = () => {
      if (state.status === 'offline') {
        dispatch({ type: 'SET_STATUS', payload: 'idle' });
        // Trigger sync when back online
        performSync();
      }
    };

    const handleOffline = () => {
      dispatch({ type: 'SET_STATUS', payload: 'offline' });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial state
    if (!navigator.onLine) {
      dispatch({ type: 'SET_STATUS', payload: 'offline' });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [state.status, performSync]);

  const value: SyncContextValue = {
    status: state.status,
    lastSyncAt: state.lastSyncAt,
    lastResult: state.lastResult,
    error: state.error,
    retryQueueLength: state.retryQueue.length,
    isSyncing: state.status === 'syncing',
    triggerSync: performSync,
    queueAutoSave,
    clearError,
  };

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}
